import type { components } from "@sagot/api-client";
import type {
  AttributeObject,
  ClickFrequency,
  CollectFieldType,
  CollectReplyFormat,
  ConditionOperator,
  ConversationPriority,
  HandToTarget,
  LeadOwnerMode,
  NotifiableCategory,
  NotifyDelivery,
  NotifyTarget,
  OutOfHours,
  PageFrequency,
  PageUrlOperator,
  RatingScale,
  RoundRobinSetting,
  RouteMode,
  ScheduleDay,
  SetLanguageChoice,
  WaitInterrupt,
  WaitUnit,
  WatchedAttribute,
  WebhookEventKey,
  WorkflowGoalEvent,
  WorkflowSchedule,
  WorkflowTrigger,
} from "@sagot/constants";
import type { ServiceStep } from "./service/service-types";

export type * from "./service/service-types";

type Schemas = components["schemas"];

export type WorkflowOut = Schemas["WorkflowOut"];
export type WorkflowDetail = Schemas["WorkflowDetail"];
export type RunOut = Schemas["RunOut"];
export type RunDetail = Schemas["RunDetail"];
export type RunPage = Schemas["RunPageOut"];
export type WorkflowStats = Schemas["WorkflowStatsOut"];
export type WebhookEndpoint = Schemas["WebhookOut"];
export type PreviewStart = Schemas["WorkflowPreviewOut"];
/** What `POST /v1/workflows/{id}/preview/outcome` answers: the run after the chosen outcome and what it said next. */
export type PreviewOutcomeResult = Schemas["WorkflowOutcomeOut"];
export type DryRunResult = Schemas["WorkflowDryRunOut"];
export type DryRunStep = Schemas["WorkflowDryRunStepOut"];

/** The definition the builder edits (backend `app/services/workflows/definition.py`). */
export interface Condition {
  field: string;
  op: ConditionOperator;
  value?: string | number | boolean | string[] | null;
}
export interface ConditionGroup {
  all: Condition[];
}
export interface Conditions {
  any: ConditionGroup[];
}

interface StepBase {
  id: string;
  name?: string;
}
export interface SendWebhookStep extends StepBase {
  type: "send_webhook";
  endpoint_id: string;
  body: "standard" | "custom";
  template?: string;
  message?: string;
}
export interface TagsStep extends StepBase {
  type: "add_tags" | "remove_tags";
  tags: string[];
}
export interface AssignStep extends StepBase {
  type: "assign";
  to: "team" | "user" | "unassign";
  team_id?: string;
  user_id?: string;
}
export interface StatusStep extends StepBase {
  type: "set_status";
  status: "open" | "waiting" | "resolved" | "closed";
}
export interface NoteStep extends StepBase {
  type: "add_note";
  text: string;
}
export interface LeadStep extends StepBase {
  type: "update_lead";
  stage_id?: string;
  owner_user_id?: string;
  sync_crm: boolean;
}
export interface NotifyStep extends StepBase {
  type: "notify_staff";
  to: "user" | "team" | "owners_admins" | "assignee" | "lead_owner";
  user_id?: string;
  team_id?: string;
  title: string;
  body?: string;
  email: boolean;
}
/** Phase 3 behind-the-scenes steps (contract WORKFLOWS_P3_P4_CONTRACT.md). */
export interface ApplySlaStep extends StepBase {
  type: "apply_sla";
  policy_id: string;
}
export interface SetPriorityStep extends StepBase {
  type: "set_priority";
  priority: ConversationPriority;
}
export interface EmailVisitorStep extends StepBase {
  type: "email_visitor";
  subject: string;
  body: string;
}
/** `value` is a literal or a template (`{{visitor.name}}`); the server converts it to the attribute type. */
export interface SetAttributeStep extends StepBase {
  type: "set_attribute";
  object: AttributeObject;
  key: string;
  value: string;
}
/** Phase 4: runs a reusable workflow (Pro) on the same conversation, then carries on. */
export interface RunWorkflowStep extends StepBase {
  type: "run_workflow";
  workflow_id: string;
}
/** Phase 5 (P5-4): sets the conversation's language (`auto` goes back to detecting it); automatic detection never overwrites a chosen one. */
export interface SetLanguageStep extends StepBase {
  type: "set_language";
  language: SetLanguageChoice;
}
/** Phase 5 (P5-6): writes an AI summary of the conversation as an internal note (Pro; one AI credit); the visitor never sees it. */
export interface AddSummaryNoteStep extends StepBase {
  type: "add_summary_note";
  notify_assignee: boolean;
}
export type ActionStep =
  | SendWebhookStep
  | TagsStep
  | AssignStep
  | StatusStep
  | NoteStep
  | LeadStep
  | NotifyStep
  | ApplySlaStep
  | SetPriorityStep
  | EmailVisitorStep
  | SetAttributeStep
  | RunWorkflowStep
  | SetLanguageStep
  | AddSummaryNoteStep;

export interface Branch {
  label: string;
  when: Conditions;
  goto: string;
}
export interface BranchStep extends StepBase {
  type: "branch";
  branches: Branch[];
  else_goto?: string;
}
export interface Rule {
  label: string;
  when: Conditions;
  actions: ActionStep[];
}
export interface RulesStep extends StepBase {
  type: "apply_rules";
  rules: Rule[];
}
/** A length of time; a wait, a snooze, a reply timeout and a card timeout all use it. */
export interface StepDuration {
  value: number;
  unit: WaitUnit;
}
export interface WaitStep extends StepBase {
  type: "wait";
  /** Missing = "until the office opens" (needs `until_office_hours`): no length of its own, skipped when the office is already open. */
  duration?: StepDuration;
  until_office_hours?: boolean;
  interrupt_on: WaitInterrupt[];
  interrupted_goto?: string;
}
/** Phase 5 (P5-4): snoozes the conversation for a while (`for`), until a moment (`until`, workspace time zone) or until the office opens; exactly one of the three. */
export interface SnoozeUntil {
  /** `YYYY-MM-DD`. */
  date: string;
  /** `HH:MM`. */
  time: string;
}
export interface SnoozeStep extends StepBase {
  type: "snooze";
  for?: { value: number; unit: WaitUnit };
  until?: SnoozeUntil;
  until_office_hours?: boolean;
  /** When it wakes on time (unset = the next step). */
  woke_goto?: string;
  /** When the customer or a teammate replies first, or the conversation closes (unset = the workflow ends). */
  interrupted_goto?: string;
}
/** What the client's system may send back: flat, scalar values (the builder only uses the keys as variable names). */
export type HookExampleValue = string | number | boolean | null;
/** Phase 4: pauses until the client's system calls the run's signed URL, or `timeout_hours` pass. */
export interface WaitForWebhookStep extends StepBase {
  type: "wait_for_webhook";
  timeout_hours: number;
  example_body?: Record<string, HookExampleValue>;
  received_goto?: string;
  timeout_goto?: string;
}
/** Phase 5 (P5-5): the Data connector. `path`, `query` and `body` take `{{variables}}`; the credentials live on the endpoint, never here. */
export interface HttpRequestMapping {
  /** An RFC 6901 JSON Pointer into the response (`/data/status`). */
  from: string;
  /** The run variable that receives it (`var.enrol_status`). */
  to: string;
}
/** The legacy inline form of the step: a webhook endpoint, the request and the answer's map. */
export interface HttpRequestStep extends StepBase {
  type: "http_request";
  endpoint_id: string;
  method: "GET" | "POST" | "PUT" | "PATCH";
  path?: string;
  query?: Record<string, string>;
  headers?: Record<string, string>;
  body?: string;
  timeout_ms: number;
  map?: HttpRequestMapping[];
  success_goto?: string;
  failure_goto?: string;
  connector_id?: undefined;
}
/** One declared output of an operation (a `variable` field of its response mapping) copied into the run variable `to`. */
export interface ConnectorCallOutput {
  name: string;
  to: string;
}
/**
 * SD-SC-12: "Call connector operation". Names a connector of the Connectors platform and one of its operations; `params`
 * are templates over the run's facts, `outputs` choose and rename the operation's declared outputs. A failure leaves by the
 * exit of its kind (`not_found_goto`, `rejected_goto`, `unavailable_goto`), each falling back to `failure_goto`.
 */
export interface ConnectorCallStep extends StepBase {
  type: "http_request";
  connector_id: string;
  operation_key: string;
  /** A published version to pin (absent: follow the one the operation points at). */
  version?: number;
  params?: Record<string, string>;
  outputs?: ConnectorCallOutput[];
  success_goto?: string;
  failure_goto?: string;
  not_found_goto?: string;
  rejected_goto?: string;
  unavailable_goto?: string;
  endpoint_id?: undefined;
}
/** Either form of the `http_request` step; `connector_id` tells them apart. */
export type AnyHttpRequestStep = HttpRequestStep | ConnectorCallStep;
export const isConnectorCall = (step: AnyHttpRequestStep): step is ConnectorCallStep => step.connector_id !== undefined;
export interface GoToStep extends StepBase {
  type: "go_to_path";
  path: string;
}
export interface EndStep extends StepBase {
  type: "end";
}

/** Customer-facing steps (runtime contract §3). A `goto` of `""` means "not chosen yet"; an absent exit continues with the next step. */
export interface SendMessageStep extends StepBase {
  type: "send_message";
  text: string;
}
export interface ReplyButton {
  id: string;
  label: string;
  messenger_label?: string;
  goto: string;
}
export interface ReplyButtonsStep extends StepBase {
  type: "reply_buttons";
  text: string;
  buttons: ReplyButton[];
  allow_typing: boolean;
  /** Saves the chosen button's label (in the workflow's own language) to `var.<key>` or a text attribute. */
  save_to?: string;
  /** Adds a last button, "Start over", that goes back to Path A (it counts toward the 8 buttons). */
  start_over?: boolean;
}
export interface CollectOption {
  id: string;
  label: string;
}
export interface CollectField {
  type: CollectFieldType;
  label: string;
  options?: CollectOption[];
  required: boolean;
}
export interface CollectDataStep extends StepBase {
  type: "collect_data";
  text: string;
  field: CollectField;
  save_to: string;
  skip_if_known: boolean;
}
export interface LetAssistantAnswerStep extends StepBase {
  type: "let_assistant_answer";
  guidance?: string;
  answered_goto?: string;
  not_answered_goto?: string;
  asked_for_person_goto?: string;
}
export interface ShowBookingStep extends StepBase {
  type: "show_booking";
  action_id?: string;
  booked_goto?: string;
  not_booked_goto?: string;
  /** The card closes for the visitor after this long without a booking (missing = 30 minutes) and the run takes the not booked exit. */
  not_booked_after?: StepDuration;
}
export interface ShowLeadFormStep extends StepBase {
  type: "show_lead_form";
  action_id?: string;
  submitted_goto?: string;
  not_submitted_goto?: string;
  /** The form closes for the visitor after this long unsent (missing = 30 minutes) and the run takes the not submitted exit. */
  not_submitted_after?: StepDuration;
}
export interface HandToPersonStep extends StepBase {
  type: "hand_to_person";
  to: HandToTarget;
  team_id?: string;
  user_id?: string;
  reason?: string;
}
export interface ExpectedReplyTimeStep extends StepBase {
  type: "expected_reply_time";
}
export interface AskRatingStep extends StepBase {
  type: "ask_rating";
  text: string;
  scale: RatingScale;
  ask_comment: boolean;
  rated_goto?: string;
}
/** Phase 5 (P5-4): asks `text` and saves the visitor's next typed message; it never reaches the assistant. */
export interface CollectReplyStep extends StepBase {
  type: "collect_reply";
  text: string;
  save_to: string;
  format: CollectReplyFormat;
  min_length: number;
  max_length: number;
  no_reply_after?: { value: number; unit: WaitUnit };
  /** After an answer (unset = the next step). */
  replied_goto?: string;
  /** When nobody answers in time; only with `no_reply_after` (unset = the workflow ends). */
  no_reply_goto?: string;
}
/** Phase 5 (P5-4): lock and unlock the visitor's chat composer. */
export interface DisableReplyStep extends StepBase {
  type: "disable_reply";
}
export interface EnableReplyStep extends StepBase {
  type: "enable_reply";
}
/** Phase 5 (P5-6): a card that links a knowledge source (or one page of a crawled website) to the visitor. */
export interface ShowArticleStep extends StepBase {
  type: "show_article";
  source_id: string;
  /** A page of a crawled website; its link wins over the source's. */
  page_id?: string;
  text?: string;
  /** The button's words (unset = "Open article"). */
  label?: string;
}
/** Phase 5 (P5-7): e-mails a one-time code to the address the run already knows and waits for the visitor to type it. */
export interface VerifyEmailStep extends StepBase {
  type: "verify_email";
  text: string;
  /** After a correct code (unset = the next step). */
  verified_goto?: string;
  /** No address, wrong codes, expired code, too many messages that are no code (unset = the workflow ends). */
  failed_goto?: string;
}
export type FacingStep =
  | SendMessageStep
  | ReplyButtonsStep
  | CollectDataStep
  | LetAssistantAnswerStep
  | ShowBookingStep
  | ShowLeadFormStep
  | HandToPersonStep
  | ExpectedReplyTimeStep
  | AskRatingStep
  | CollectReplyStep
  | VerifyEmailStep
  | DisableReplyStep
  | EnableReplyStep
  | ShowArticleStep;

/** Phase 6 (DEC-099): the steps that make a workflow the source of truth for staff alerts, assignment and webhook delivery. */
export interface NotifyRecipient {
  target: NotifyTarget;
  /** `direct` hands the notice to a member personally (only the assignee and the handoff audiences); unset follows each member's own settings. */
  delivery?: NotifyDelivery;
  team_id?: string;
  user_id?: string;
  permission?: string;
}
/** The exact wording today's code sends for the trigger, or one written in the workspace. */
export type NotifyMessage = { template: "system" } | { template: "custom"; title: string; body?: string };
export interface NotifyTeamStep extends StepBase {
  type: "notify_team";
  category: NotifiableCategory;
  to: NotifyRecipient[];
  /** The member who did the thing needs no notice of it. */
  exclude_actor?: boolean;
  /** A cap on the channels (a member's own settings can only narrow it further). */
  channels?: ("in_app" | "email")[];
  channels_from?: "source_notice";
  out_of_hours?: OutOfHours;
  message: NotifyMessage;
}
/** Chooses who a new handoff goes to, in the handoff transaction (a default only). */
export interface RouteHandoffStep extends StepBase {
  type: "route_handoff";
  mode: RouteMode;
  team_id?: string;
  user_id?: string;
  round_robin?: RoundRobinSetting;
}
/** Chooses who a new lead goes to, in the capture transaction (a default only). */
export interface AssignLeadOwnerStep extends StepBase {
  type: "assign_lead_owner";
  mode: LeadOwnerMode;
  user_id?: string;
}
/** Delivers the event to the endpoints that subscribe to it; it has no settings of its own (a default only). */
export interface SendToSubscribedEndpointsStep extends StepBase {
  type: "send_to_subscribed_endpoints";
}
export type SystemStep = NotifyTeamStep | RouteHandoffStep | AssignLeadOwnerStep | SendToSubscribedEndpointsStep;

export type Step = ActionStep | BranchStep | RulesStep | WaitStep | WaitForWebhookStep | HttpRequestStep | ConnectorCallStep | SnoozeStep | GoToStep | EndStep | FacingStep | SystemStep | ServiceStep;

export interface PageUrlRule {
  op: PageUrlOperator;
  value: string;
}
/** `settings.trigger_options`: minutes for the unresponsive triggers, URL rules for a page visit or a click, the watched attribute, the clicked element. */
export interface TriggerOptions {
  minutes?: number;
  url_rules?: PageUrlRule[];
  seconds_on_page?: number;
  frequency?: PageFrequency | ClickFrequency;
  /** `attribute_changed`: the custom attribute that is watched. */
  attribute?: WatchedAttribute;
  /** `visitor_clicks_element`: up to five safe selectors, comma separated. */
  selector?: string;
  /** `platform_event` (the Developer webhooks default): every event the endpoints subscribe to, or only these. */
  events?: "all" | WebhookEventKey[];
}
export interface TimeRange {
  from: string;
  to: string;
}
/** `settings.custom_schedule`: weekly opening ranges per day (a missing or empty day is closed). */
export interface CustomSchedule {
  timezone?: string;
  days: Partial<Record<ScheduleDay, TimeRange[]>>;
}

/** `settings.goal`: the event that counts as success, and how long after the run started it may happen. */
export interface WorkflowGoal {
  event: WorkflowGoalEvent;
  within_hours: number;
}

export interface WorkflowSettings {
  agent_ids: string[];
  channels: string[];
  schedule: WorkflowSchedule;
  conditions: Conditions;
  trigger_options?: TriggerOptions;
  custom_schedule?: CustomSchedule;
  goal?: WorkflowGoal;
  /** `home_option` workflows: the Widget Home option that starts this one. */
  option_id?: string;
}
export interface PathDef {
  label?: string;
  steps: Step[];
}
/**
 * A workflow definition as the builder edits it. The OpenAPI schema documents `definition` as a free-form object, so
 * this hand-written shape (mirroring backend `services/workflows/definition.py`) stays; the server validates it.
 */
export interface Definition {
  settings: WorkflowSettings;
  paths: Record<string, PathDef>;
  translations?: Translations;
  notes?: Notes;
}

/** A sticky note of the builder (`definition.notes`): for the people who edit the workflow, never shown to a visitor. `path` anchors it to a path. */
export interface Note {
  text: string;
  path?: string;
}
/** `definition.notes`: `{note id: note}`. */
export type Notes = Record<string, Note>;

/** One step's visitor-facing texts in one language; what is missing falls back to the primary (English) text. */
export interface StepTranslation {
  text?: string;
  buttons?: Record<string, string>;
  field_label?: string;
  /** The label of an article card's button. */
  label?: string;
}
/** `definition.translations`: `{language: {step id: texts}}`. */
export type Translations = Record<string, Record<string, StepTranslation>>;

export type { WorkflowTrigger };
